// 2026-06-25: Phase 10 — ECN list + detail + create-from-ECR + issue + implement
import { cleanCustomerId } from '../../Services/app/operatorservice';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  listEcns, getEcnDetail, createEcn, issueEcn, implementEcn,
  listEcrs, listBoms, getBomDetail,
} from '../../Services/app/workorderservice';
import Swal from 'sweetalert2';
import '../NcLog/NcLog.css';
import '../Bom/Bom.css';

const STATUS_COLOR = {
  DRAFT: '#9ca3af', ISSUED: '#2563eb', IMPLEMENTED: '#16a34a', CANCELLED: '#6b7280',
};

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.toISOString().slice(0, 16).replace('T', ' ');
}

export default function EcnList() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [rows, setRows] = useState([]);
  const [ecrs, setEcrs] = useState([]);
  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('OPEN');
  const [showCreate, setShowCreate] = useState(false);
  const [opening, setOpening] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        listEcns(customerId),
        listEcrs(customerId, 'APPROVED'),
        listBoms(customerId),
      ]);
      setRows(a || []);
      setEcrs(b || []);
      setBoms(c || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [customerId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return rows;
    if (filter === 'OPEN') return rows.filter((r) => ['DRAFT', 'ISSUED'].includes(r.status));
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(() => ({
    ALL: rows.length,
    OPEN: rows.filter((r) => ['DRAFT', 'ISSUED'].includes(r.status)).length,
    IMPLEMENTED: rows.filter((r) => r.status === 'IMPLEMENTED').length,
  }), [rows]);

  return (
    <div className="bom-page pages" style={{ paddingTop: 32 }}>
      <div className="nc-header">
        <div>
          <h5 style={{ margin: 0 }}>Engineering Change Notices (ECN)</h5>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Issued by Engineering · Implementation bumps BOM rev + pushes Epicor
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="nc-btn-secondary" onClick={fetchAll}>Refresh</button>
          <button className="nc-btn-primary" onClick={() => setShowCreate(true)}>+ New ECN</button>
        </div>
      </div>

      <div className="nc-tabs">
        {['OPEN', 'IMPLEMENTED', 'ALL'].map((t) => (
          <button key={t} className={`nc-tab ${filter === t ? 'nc-tab-active' : ''}`} onClick={() => setFilter(t)}>
            {t} <span className="nc-tab-count">{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? <div style={{ padding: 30, color: '#9ca3af' }}>Loading…</div> :
        filtered.length === 0 ? <div style={{ padding: 30, color: '#9ca3af' }}>No ECNs in this view.</div> : (
        <div className="nc-grid ecn-list">
          {filtered.map((r) => (
            <div key={r.id} className="nc-card" style={{ borderLeftColor: STATUS_COLOR[r.status] }}>
              <div className="nc-card-head">
                <div>
                  <div className="nc-num">{r.ecn_number}</div>
                  <div className="nc-meta">{fmtTime(r.created_ts)}</div>
                </div>
                <span className="nc-pill" style={{ background: STATUS_COLOR[r.status] }}>{r.status}</span>
              </div>
              <div style={{ fontWeight: 700, color: '#111827', fontSize: 13, margin: '4px 0' }}>{r.title}</div>
              <div className="nc-row"><span>Part / Rev</span><strong>{r.affected_part_number} ({r.from_revision} → {r.to_revision || '?'})</strong></div>
              <div className="nc-row"><span>Source ECR</span><strong>{r.ecr_number || '(none)'}</strong></div>
              <div className="nc-row"><span>Prepared by</span><strong>{r.prepared_by}</strong></div>
              <div className="nc-row"><span>Change lines</span><strong>{r.change_count}</strong></div>
              {r.status === 'IMPLEMENTED' && (
                <div style={{ marginTop: 8, padding: 8, background: '#f0fdf4', borderRadius: 6, fontSize: 11, color: '#166534' }}>
                  ✓ {fmtTime(r.implemented_at)} · {r.implemented_by} · Epicor pushed ({r.epicor_mode})
                </div>
              )}
              <div className="nc-actions">
                <button className="nc-btn-primary" onClick={() => setOpening(r)}>Open</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <EcnCreateDialog ecrs={ecrs} boms={boms} customerId={customerId}
                         onClose={() => setShowCreate(false)}
                         onCreated={() => { setShowCreate(false); fetchAll(); }} />
      )}
      {opening && (
        <EcnDetailDialog ecnRow={opening} customerId={customerId}
                         onClose={() => setOpening(null)}
                         onRefresh={fetchAll} />
      )}
    </div>
  );
}

function EcnCreateDialog({ ecrs, boms, customerId, onClose, onCreated }) {
  const [ecrId, setEcrId] = useState('');
  const [bomId, setBomId] = useState('');
  const [bomDetail, setBomDetail] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', prepared_by: '', effective_date: '' });
  const [changeLines, setChangeLines] = useState([]);
  const [saving, setSaving] = useState(false);

  // When ECR is picked, auto-fill some fields
  useEffect(() => {
    if (!ecrId) return;
    const ecr = ecrs.find((e) => e.id === ecrId);
    if (ecr) {
      setForm((f) => ({ ...f, title: ecr.title, description: `Implementation of ${ecr.ecr_number}: ${ecr.proposed_change || ecr.reason}` }));
      // Find the matching ACTIVE BOM
      const bom = boms.find((b) => b.part_number === ecr.affected_part_number && b.status === 'ACTIVE');
      if (bom) setBomId(bom.id);
    }
  }, [ecrId, ecrs, boms]);

  // Load BOM detail when BOM picked
  useEffect(() => {
    if (!bomId) { setBomDetail(null); return; }
    getBomDetail(customerId, bomId).then(setBomDetail).catch(() => setBomDetail(null));
  }, [bomId, customerId]);

  const addLine = (action) => setChangeLines([...changeLines, {
    action, item_part_number: '', item_description: '',
    old_qty: '', new_qty: '', old_uom: '', new_uom: 'EACH', reason: '',
  }]);
  const updateLine = (i, k, v) => setChangeLines(changeLines.map((l, ix) => ix === i ? { ...l, [k]: v } : l));
  const removeLine = (i) => setChangeLines(changeLines.filter((_, ix) => ix !== i));

  const submit = async () => {
    if (!bomId || !form.title || !form.prepared_by || changeLines.length === 0) {
      Swal.fire('Missing', 'BOM, title, prepared-by, and at least one change line required', 'warning'); return;
    }
    setSaving(true);
    try {
      const ecr = ecrs.find((e) => e.id === ecrId);
      await createEcn({
        customer_id: customerId,
        ecr_id: ecrId || undefined,
        affected_part_number: ecr?.affected_part_number || bomDetail?.header.part_number,
        from_bom_id: bomId,
        title: form.title,
        description: form.description,
        prepared_by: form.prepared_by,
        effective_date: form.effective_date || undefined,
        change_lines: changeLines.map((l) => ({
          ...l,
          old_qty: l.old_qty === '' ? undefined : Number(l.old_qty),
          new_qty: l.new_qty === '' ? undefined : Number(l.new_qty),
        })),
      });
      Swal.fire({ icon: 'success', title: 'ECN created', timer: 1300, showConfirmButton: false });
      onCreated();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal bom-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head"><h6>New ECN</h6><button onClick={onClose}>×</button></div>
        <div className="nc-modal-body">
          <label>Source ECR (optional)</label>
          <select value={ecrId} onChange={(e) => setEcrId(e.target.value)}>
            <option value="">— No ECR (direct ECN) —</option>
            {ecrs.map((e) => <option key={e.id} value={e.id}>{e.ecr_number} — {e.title}</option>)}
          </select>
          <label>Target BOM (rev that will be replaced) *</label>
          <select value={bomId} onChange={(e) => setBomId(e.target.value)}>
            <option value="">— Pick active BOM —</option>
            {boms.filter((b) => b.status === 'ACTIVE').map((b) => (
              <option key={b.id} value={b.id}>{b.part_number} rev {b.revision}</option>
            ))}
          </select>
          {bomDetail && (
            <div style={{ background: '#f9fafb', padding: 8, borderRadius: 4, fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              Current rev {bomDetail.header.revision} has {bomDetail.lines.length} lines: {bomDetail.lines.map((l) => l.item_part_number).join(', ')}
            </div>
          )}
          <div className="nc-grid-2">
            <div><label>Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><label>Prepared by *</label><input value={form.prepared_by} onChange={(e) => setForm({ ...form, prepared_by: e.target.value })} /></div>
          </div>
          <label>Description</label>
          <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <label>Effective Date (optional)</label>
          <input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />

          <h6 style={{ marginTop: 14, marginBottom: 4 }}>Change Lines</h6>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button type="button" className="nc-btn-secondary" onClick={() => addLine('ADD')}>+ Add</button>
            <button type="button" className="nc-btn-secondary" onClick={() => addLine('REMOVE')}>− Remove</button>
            <button type="button" className="nc-btn-secondary" onClick={() => addLine('MODIFY')}>± Modify</button>
          </div>
          {changeLines.length === 0 && <div style={{ color: '#9ca3af', fontSize: 12, padding: 10 }}>No change lines yet. Pick ADD / REMOVE / MODIFY above.</div>}
          {changeLines.map((l, i) => (
            <div key={i} className="ecn-change-line" style={{ flexWrap: 'wrap', gap: 6 }}>
              <span className={`action-pill ${l.action}`}>{l.action}</span>
              <input placeholder="Part #" value={l.item_part_number}
                     onChange={(e) => updateLine(i, 'item_part_number', e.target.value)} style={{ width: 110, padding: '4px 8px' }} />
              <input placeholder="Description" value={l.item_description}
                     onChange={(e) => updateLine(i, 'item_description', e.target.value)} style={{ flex: 1, padding: '4px 8px' }} />
              {l.action !== 'ADD' && (
                <input placeholder="old qty" value={l.old_qty} type="number"
                       onChange={(e) => updateLine(i, 'old_qty', e.target.value)} style={{ width: 70, padding: '4px 8px' }} />
              )}
              {l.action !== 'REMOVE' && (
                <input placeholder="new qty" value={l.new_qty} type="number"
                       onChange={(e) => updateLine(i, 'new_qty', e.target.value)} style={{ width: 70, padding: '4px 8px' }} />
              )}
              <button type="button" onClick={() => removeLine(i)} className="bom-row-del">×</button>
            </div>
          ))}
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="nc-btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Create ECN (DRAFT)'}</button>
        </div>
      </div>
    </div>
  );
}

function EcnDetailDialog({ ecnRow, customerId, onClose, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [actor, setActor] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getEcnDetail(customerId, ecnRow.id).then(setDetail);
  }, [customerId, ecnRow.id]);

  const doIssue = async () => {
    if (!actor) { Swal.fire('Missing', 'Your name required', 'warning'); return; }
    setSaving(true);
    try {
      await issueEcn(ecnRow.id, { customer_id: customerId, approved_by: actor });
      Swal.fire({ icon: 'success', title: 'ECN issued', timer: 1300, showConfirmButton: false });
      onClose(); onRefresh();
    } catch (e) { Swal.fire('Error', e?.response?.data?.message || e.message, 'error'); }
    setSaving(false);
  };

  const doImplement = async () => {
    if (!actor) { Swal.fire('Missing', 'Your name required', 'warning'); return; }
    const c = await Swal.fire({
      title: 'Implement ECN?',
      text: 'This will create a new BOM revision and push to Epicor (DRY mode).',
      icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, implement',
    });
    if (!c.isConfirmed) return;
    setSaving(true);
    try {
      const r = await implementEcn(ecnRow.id, { customer_id: customerId, implemented_by: actor });
      Swal.fire({
        icon: 'success',
        title: `BOM bumped to Rev ${r.new_bom.revision}`,
        html: `<div style="text-align:left;font-size:12px;color:#374151">${r.epicor_message}</div>`,
      });
      onClose(); onRefresh();
    } catch (e) { Swal.fire('Error', e?.response?.data?.message || e.message, 'error'); }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal bom-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head">
          <h6>{ecnRow.ecn_number} <span className="ecn-flow">{ecnRow.status}</span></h6>
          <button onClick={onClose}>×</button>
        </div>
        <div className="nc-modal-body">
          {!detail ? <div>Loading…</div> : (
          <>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{detail.ecn.title}</div>
            <div className="bom-detail-meta">
              <div><span>Part</span><strong>{detail.ecn.affected_part_number}</strong></div>
              <div><span>From rev</span><strong>{detail.ecn.from_revision}</strong></div>
              <div><span>To rev</span><strong>{detail.ecn.to_revision || '(after implement)'}</strong></div>
              <div><span>Source ECR</span><strong>{detail.ecn.ecr_number || '—'}</strong></div>
              <div><span>Prepared by</span><strong>{detail.ecn.prepared_by}</strong></div>
              <div><span>Issued by</span><strong>{detail.ecn.approved_by || '—'}</strong></div>
              <div><span>Implemented by</span><strong>{detail.ecn.implemented_by || '—'}</strong></div>
              <div><span>Epicor</span><strong>{detail.ecn.epicor_pushed ? `✓ ${detail.ecn.epicor_mode}` : '—'}</strong></div>
            </div>
            <h6 style={{ marginTop: 14, marginBottom: 6 }}>Change Lines</h6>
            {detail.change_lines.map((cl) => (
              <div key={cl.id} className="ecn-change-line">
                <span className={`action-pill ${cl.action}`}>{cl.action}</span>
                <strong>{cl.item_part_number}</strong>
                <span style={{ color: '#6b7280' }}>{cl.item_description}</span>
                {cl.action === 'MODIFY' && <span>{cl.old_qty} → {cl.new_qty}</span>}
                {cl.action === 'ADD' && <span>qty {cl.new_qty} {cl.new_uom}</span>}
                {cl.action === 'REMOVE' && <span>was qty {cl.old_qty}</span>}
                {cl.reason && <span style={{ marginLeft: 'auto', fontSize: 11, fontStyle: 'italic' }}>{cl.reason}</span>}
              </div>
            ))}
            {detail.ecn.status !== 'IMPLEMENTED' && detail.ecn.status !== 'CANCELLED' && (
              <>
                <label style={{ marginTop: 14 }}>Your name *</label>
                <input value={actor} onChange={(e) => setActor(e.target.value)} />
              </>
            )}
          </>)}
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Close</button>
          {detail?.ecn.status === 'DRAFT' && (
            <button className="nc-btn-primary" onClick={doIssue} disabled={saving}>{saving ? '…' : 'Issue ECN'}</button>
          )}
          {detail?.ecn.status === 'ISSUED' && (
            <button className="nc-btn-primary" onClick={doImplement} disabled={saving} style={{ background: '#16a34a', borderColor: '#16a34a' }}>
              {saving ? '…' : 'Implement → New BOM rev + Epicor push'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
